import type { Post } from "./types";

export const POSTS_5: Record<string, Post> = {
  "sms-lan-utan-uc": {
    title: "SMS-lån utan UC – Så hittar du det säkraste alternativet 2026",
    intro:
      "SMS-lån utan UC innebär att långivaren gör sin kreditkontroll via ett annat kreditupplysningsföretag, till exempel Creditsafe eller Bisnode, istället för UC. Sedan lagändringen 2025 gäller samma strikta ränte- och kostnadstak för alla svenska snabblån, vilket gör marknaden betydligt tryggare än tidigare. Här går vi igenom hur du hittar ett säkert SMS-lån utan UC, vad det får kosta enligt lag, och vilka fallgropar du bör undvika när det krisar sig ekonomiskt.",
    blocks: [
      { t: "h2", text: "Vad är ett SMS-lån utan UC?" },
      {
        t: "p",
        text:
          "Ett SMS-lån är ett litet lån utan säkerhet som du ansöker om direkt i mobilen, vanligtvis mellan 500 och 5 000 kronor. Namnet kommer från att pengarna historiskt betalades ut och aviserades via SMS, men i dag sker det mesta via BankID och app."
      },
      {
        t: "p",
        text:
          "\"Utan UC\" betyder inte att ingen kreditprövning görs. Det innebär bara att långivaren hämtar sin upplysning från ett annat kreditupplysningsföretag än UC AB, exempelvis Creditsafe eller Bisnode. Alla seriösa långivare är enligt lag skyldiga att göra en kreditbedömning innan de beviljar ett lån – oavsett vilket bolag de använder. Läs mer i vår guide om [snabblån utan UC](/nyhet/snabblan-utan-uc)."
      },
      { t: "h3", text: "Därför söker många SMS-lån utan UC" },
      {
        t: "ul",
        items: [
          "Färre synliga UC-förfrågningar – kan vara relevant om du planerar en bolåneansökan inom kort",
          "Snabbare handläggning hos vissa mindre aktörer",
          "Möjlighet att låna trots flera tidigare UC-förfrågningar"
        ]
      },
      {
        t: "p",
        text:
          "Vill du se aktuella räntor, lånebelopp och villkor från licensierade långivare innan du ansöker? Jämför alla [SMS-lån på ett ställe](/smslan)."
      },
      { t: "h2", text: "Säkraste SMS-lånet när det krisar sig – vad bör du välja?" },
      {
        t: "p",
        text:
          "Om ekonomin är akut ansträngd är det lätt att ansöka hos den första långivaren som dyker upp i en sökning. Men i en krissituation är det extra viktigt att välja rätt, eftersom fel val kan förvärra situationen."
      },
      { t: "h3", text: "Så identifierar du ett säkert SMS-lån" },
      {
        t: "ul",
        items: [
          "Kontrollera tillståndet hos Finansinspektionen – alla svenska långivare som erbjuder konsumentkrediter ska ha tillstånd från FI eller vara registrerade som finansiella institut.",
          "Jämför den effektiva räntan (ÅOP), inte bara den nominella – den inkluderar alla avgifter och ger en rättvisande bild av vad lånet kostar.",
          "Undvik ombetalning eller \"rullning\" av lånet – att förlänga ett SMS-lån flera gånger är den vanligaste vägen in i en skuldspiral.",
          "Se till att långivaren gör en seriös kreditprövning – ett företag som beviljar lån utan att kontrollera din betalningsförmåga bryter mot konsumentkreditlagen.",
          "Låna aldrig mer än du absolut behöver – ett mindre lån med kort löptid är alltid säkrare än att låna maxbeloppet \"för säkerhets skull\"."
        ]
      },
      {
        t: "p",
        text:
          "Om krisen handlar om att du inte kan betala löpande räkningar bör du i första hand kontakta budget- och skuldrådgivningen i din kommun – den är kostnadsfri och kan i vissa fall vara ett bättre alternativ än att ta ett nytt lån."
      },
      { t: "h2", text: "Vad får ett SMS-lån kosta enligt lag?" },
      {
        t: "p",
        text:
          "Sedan den 1 mars 2025 gäller skärpta regler i konsumentkreditlagen (2010:1846) som omfattar alla konsumentkrediter utan säkerhet – inte bara det som tidigare kallades högkostnadskrediter. Det ger dig som låntagare ett starkt lagstadgat skydd."
      },
      { t: "h3", text: "De tre viktigaste taken" },
      {
        t: "ul",
        items: [
          "Räntetak (19 a § KKrL): nominell ränta får max vara referensräntan + 20 procentenheter. Vid en referensränta på 2,00 % ger det ett tak på 22 % under första halvåret 2026.",
          "Kostnadstak (19 b § KKrL): ränta och avgifter tillsammans får aldrig överstiga lånebeloppet. Lånar du 3 000 kr betalar du alltså aldrig mer än 6 000 kr totalt.",
          "Avgiftstak: uppläggningsavgiften får max vara 1 % av prisbasbeloppet, vilket motsvarar cirka 592 kr per kreditavtal 2026."
        ]
      },
      {
        t: "p",
        text:
          "Räntetaket på 22 procent gäller från 1 januari till 30 juni 2026 och fastställs på nytt varje halvår utifrån Riksbankens referensränta. Det innebär att räntenivån kan ändras något under året – kontrollera alltid aktuell nivå hos långivaren innan du skriver under avtalet. Värt att notera: från och med 2026 går det inte längre att göra skatteavdrag för räntor på SMS-lån, vilket gör den faktiska kostnaden något högre än tidigare år. Vill du förstå grunderna djupare, läs [vad ränta är och hur det fungerar](/nyhet/vad-ar-ranta-och-hur-fungerar-det)."
      },
      { t: "h2", text: "SMS-lån med direktutbetalning – så snabbt går det" },
      {
        t: "ul",
        items: [
          "Svar inom några minuter efter genomförd ansökan med BankID",
          "Utbetalning samma dag, ofta inom en timme på vardagar",
          "Ansökningar som skickas efter kl. 22 eller på helger kan dröja till nästa bankdag"
        ]
      },
      {
        t: "p",
        text:
          "Tips: direktutbetalning är ingen garanti – vissa långivare marknadsför \"utbetalning direkt\" men har ändå viss handläggningstid vid hög belastning eller om extra kontroller krävs. Läs alltid de faktiska villkoren, inte bara annonsrubriken."
      },
      { t: "h2", text: "SMS-lån trots anmärkningar – vad gäller?" },
      {
        t: "p",
        text:
          "Har du en betalningsanmärkning blir urvalet av långivare mindre, men det är inte omöjligt att beviljas lån. Läs mer i vår guide om [betalningsanmärkning och lån](/nyhet/betalningsanmarkning-lan)."
      },
      {
        t: "ul",
        items: [
          "Antal anmärkningar – en enstaka äldre anmärkning väger lättare än flera aktiva",
          "Inkomst och betalningsförmåga – långivaren tittar på om du har utrymme att betala tillbaka, inte bara historiken",
          "Belopp – lägre lånebelopp beviljas lättare än stora summor",
          "Vilket kreditupplysningsbolag långivaren använder – vissa lägger större vikt vid UC-historik än andra"
        ]
      },
      {
        t: "p",
        text:
          "Långivare som riktar sig specifikt till personer med anmärkningar tar ofta ut ränta nära lagens tak, eftersom risken för dem är högre. Kostnadstaket på 100 % av lånebeloppet gäller dock alltid, oavsett din kredithistorik. Om du redan har en betalningsanmärkning och överväger ännu ett lån för att lösa en akut situation, ta gärna kontakt med budget- och skuldrådgivningen i din kommun först – ett nytt lån löser sällan ett grundproblem med återkommande underskott."
      },
      { t: "h2", text: "Så ansöker du om ett SMS-lån utan UC – steg för steg" },
      {
        t: "ul",
        items: [
          "Bestäm hur mycket du faktiskt behöver – låna aldrig mer \"för säkerhets skull\".",
          "Jämför flera långivare utifrån effektiv ränta, avgifter och löptid – inte bara nominell ränta.",
          "Legitimera dig med BankID och fyll i ansökan med korrekta uppgifter om inkomst och utgifter.",
          "Vänta på kreditbeslut, vanligtvis inom några minuter.",
          "Läs igenom hela låneavtalet innan du signerar – kontrollera ÅOP, total kostnad och återbetalningsplan.",
          "Ta emot utbetalningen och sätt upp en påminnelse inför första förfallodagen för att undvika dröjsmålsränta och påminnelseavgifter."
        ]
      },
      { t: "h2", text: "Vanliga frågor om SMS-lån utan UC" },
      { t: "h3", text: "Är det farligare att ta ett SMS-lån utan UC jämfört med ett vanligt lån?" },
      {
        t: "p",
        text:
          "Inte nödvändigtvis. Långivaren gör fortfarande en kreditprövning, bara via ett annat kreditupplysningsföretag. Det som avgör risken är lånevillkoren – ränta, avgifter och din egen betalningsförmåga – inte vilket kreditupplysningsbolag som används."
      },
      { t: "h3", text: "Kan jag få ett SMS-lån utan UC om jag har flera betalningsanmärkningar?" },
      {
        t: "p",
        text:
          "Det beror på antalet anmärkningar, din inkomst och vilken långivare du väljer. Vissa aktörer beviljar mindre lånebelopp trots anmärkningar, men chansen minskar ju fler aktiva anmärkningar du har."
      },
      { t: "h3", text: "Vad är skillnaden mellan SMS-lån och snabblån?" },
      {
        t: "p",
        text:
          "Begreppen används ofta synonymt. Historiskt syftade \"SMS-lån\" på mindre belopp (500–5 000 kr) med kort löptid, medan \"snabblån\" kunde omfatta något större belopp. Sedan 2025 gäller samma räntetak och kostnadstak för båda kategorierna."
      },
      { t: "h3", text: "Hur snabbt får jag pengarna vid ett SMS-lån?" },
      {
        t: "p",
        text:
          "De flesta ansökningar besvaras inom några minuter, och utbetalning sker ofta samma dag – förutsatt att ansökan görs under bankens öppettider och att inga extra kontroller krävs."
      },
      { t: "h2", text: "Viktigt att veta" },
      {
        t: "p",
        text:
          "Att låna pengar kostar alltid pengar, och ett SMS-lån bör endast användas för kortsiktiga, väl övervägda behov. Om du har svårt att betala dina räkningar eller upplever att skulderna växer, kontakta budget- och skuldrådgivningen i din kommun eller läs mer på Konsumentverket.se. Vid spelrelaterad skuldsättning finns stöd att få hos Stödlinjen."
      }
    ],
    cta: { to: "/smslan", label: "Se alla SMS-lån här" },
    topLenderId: "creditstar",
    bottomLenderId: "ferratum"
  }
};
